import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiClient } from '@/services/api'

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await apiClient.post('/auth/login', credentials)
          const { user, token } = response.data.data
          
          // Store token in API client
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
          
          return { success: true, user }
        } catch (error) {
          const errorMessage = error.response?.data?.error?.message || 'Login failed'
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            user: null,
            token: null
          })
          throw new Error(errorMessage)
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await apiClient.post('/auth/admin/register', userData)
          const { user, token } = response.data.data
          
          // Store token in API client
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
          
          return { success: true, user }
        } catch (error) {
          const errorMessage = error.response?.data?.error?.message || 'Registration failed'
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            user: null,
            token: null
          })
          throw new Error(errorMessage)
        }
      },

      logout: () => {
        // Remove token from API client
        delete apiClient.defaults.headers.common['Authorization']
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        })
      },

      refreshUser: async () => {
        const { token } = get()
        if (!token) return
        
        try {
          const response = await apiClient.get('/auth/me')
          const user = response.data.data.user
          
          set({ user })
          return user
        } catch (error) {
          // If refresh fails, logout
          get().logout()
          throw error
        }
      },

      clearError: () => set({ error: null }),

      // Initialize auth state from stored token
      initialize: async () => {
        set({ isLoading: true })
        const { token } = get()
        
        if (token) {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
          try {
            // Refresh user data to verify token is still valid
            await get().refreshUser()
            set({ isLoading: false })
          } catch (error) {
            // If refresh fails, clear stored auth
            get().logout()
            set({ isLoading: false })
          }
        } else {
          set({ isLoading: false })
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)

export default useAuthStore